import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Trophy, ShieldCheck, Ticket, ArrowRight } from "lucide-react";
import { LEGENDTEAM_URL, LEGENDTEAM_LOGO, LEGENDTEAM_BANNER, TESSERAMENTO } from "@/lib/constants";
import client from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCards";

const PriceCard = ({ item }) => (
  <div data-testid={`tessera-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center justify-between gap-3 rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper p-4 hover:border-legend-red transition-colors">
    <div className="min-w-0">
      <p className="font-display font-bold uppercase text-sm leading-tight truncate">{item.name}</p>
      <p className="text-xs text-slate-500">{item.note}</p>
    </div>
    <span className="font-display font-black text-2xl text-legend-red shrink-0">{item.price}€</span>
  </div>
);

const PriceGroup = ({ icon: Icon, title, items }) => (
  <div>
    <h3 className="font-display font-bold uppercase text-lg mb-3 flex items-center gap-2"><Icon size={18} className="text-legend-red" /> {title}</h3>
    <div className="grid sm:grid-cols-2 gap-3">
      {items.map((it) => <PriceCard key={it.name} item={it} />)}
    </div>
  </div>
);

export default function LegendTeam() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    client.get("/articles").then((r) => {
      const list = (r.data || []).filter((a) => (a.category || "").toLowerCase().includes("legend team"));
      setPosts(list.slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="legend-team-page">
      {/* Hero */}
      <div className="relative bg-legend-ink text-white overflow-hidden">
        <img src={LEGENDTEAM_BANNER} alt="Legend Team" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 py-16 text-center flex flex-col items-center">
          <img src={LEGENDTEAM_LOGO} alt="Legend Team" data-testid="legend-team-logo" className="h-24 sm:h-32 object-contain drop-shadow-xl" />
          <p className="mt-6 max-w-2xl text-slate-200 text-base">Scuderia rally, club auto storiche e Legend Team Village a Sant'Agata Feltria. Passione, tecnica e spettacolo: corri con noi le gare del Settore Motori MSP Italia.</p>
          <a href={LEGENDTEAM_URL} target="_blank" rel="noreferrer" data-testid="legend-team-site-btn" className="mt-6 inline-flex items-center gap-2 bg-legend-red hover:bg-legend-dark text-white font-bold uppercase tracking-wide px-7 py-3 rounded-sm transition-colors">
            Visita legendteam.it <ExternalLink size={16} />
          </a>
        </div>
      </div>

      {/* Price banner */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="rounded-sm bg-gradient-to-r from-legend-red to-legend-dark text-white p-6 sm:p-8 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <span className="font-mono uppercase text-xs tracking-[0.3em] text-legend-yellow">Tesseramento 2026 · MSP Italia</span>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl leading-none mt-1">Corri con il Legend Team</h2>
            <p className="text-white/85 text-sm mt-2 max-w-xl">Tessere e licenze per partecipare alle gare del Settore Motori MSP Italia con la nostra scuderia.</p>
          </div>
          <a href={LEGENDTEAM_URL} target="_blank" rel="noreferrer" data-testid="legend-team-join-btn" className="shrink-0 inline-flex items-center gap-2 bg-white text-legend-red font-bold uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-legend-yellow hover:text-legend-ink transition-colors">
            Tesserati ora <ArrowRight size={16} />
          </a>
        </div>

        <div className="space-y-8">
          <PriceGroup icon={ShieldCheck} title="Tessere Società (nessuna visita medica)" items={TESSERAMENTO.societa} />
          <PriceGroup icon={Trophy} title="Licenze Conduttore · Automobilismo" items={TESSERAMENTO.auto} />
          <PriceGroup icon={Ticket} title="Licenze Conduttore · Motociclismo" items={TESSERAMENTO.moto} />
        </div>
        <p className="text-xs text-slate-500 mt-6 leading-relaxed">{TESSERAMENTO.note}</p>
      </section>

      {/* Legend Team stories */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-6 w-1.5 bg-legend-red" />
            <h2 className="font-display font-black uppercase text-2xl tracking-wide">Dal mondo Legend Team</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((a) => <ArticleCard key={a.slug} article={a} />)}
          </div>
        </section>
      )}
    </div>
  );
}
